/**
 * Trainer Assessment List
 *
 * Lists assessments created by trainers.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { RequestPanel, StatusBadge } from '../../components/RequestPanel';
import { Code } from '../../components/JsonViewer';
import type { ApiResult, Assessment, AssessmentListResponse } from '../../lib/types';

export function TrainerAssessmentList() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<AssessmentListResponse> | null>(null);

  const loadAssessments = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.listAssessments({ limit: 50 });
    setResult(res);
    if (res.response.data) {
      setAssessments(res.response.data.items);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
            Assessments
          </h1>
          <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
            Manage learner assessments and evaluation results
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-tertiary" onClick={loadAssessments} disabled={loading}>
            <iconify-icon icon="lucide:refresh-cw" width="16" height="16"></iconify-icon>
            Refresh
          </button>
          <Link to="/console/trainer/assessments/new" className="btn btn-primary">
            <iconify-icon icon="lucide:plus" width="16" height="16"></iconify-icon>
            Create Assessment
          </Link>
        </div>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>External Reference</th>
              <th>Status</th>
              <th>Rubric</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {assessments.map((a) => (
              <tr
                key={a.id}
                className="clickable"
                onClick={() => navigate(`/console/trainer/assessments/${a.id}`)}
              >
                <td>
                  <Code>{a.id.slice(0, 12)}...</Code>
                </td>
                <td>{a.externalReferenceId || <span className="text-muted">-</span>}</td>
                <td>
                  <StatusBadge status={a.status} />
                </td>
                <td>
                  {a.rubricVersion ? (
                    <Code>{a.rubricVersion.slice(0, 12)}...</Code>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td className="text-muted text-small">
                  {new Date(a.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {assessments.length === 0 && !loading && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <iconify-icon icon="lucide:clipboard-list" width="32" height="32" style={{ color: 'var(--slate-300)' }}></iconify-icon>
                    <span style={{ color: 'var(--slate-400)' }}>No assessments found</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}
